import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, Key, IdentificationCard, X, MagnifyingGlass,
  Power, CheckCircle, MapTrifold,
} from "@phosphor-icons/react";

const empty = {
  first_name: "",
  last_name: "",
  password: "",
  phone: "",
  assigned_route_id: "",
  status: "active",
  role: "driver",
  raven_device_id: "",
};

function genUsername(first, last) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (!f || !l) return "";
  return `ZMX${l.charAt(0)}${f}`.toUpperCase().replace(/\s+/g, "");
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  // Raven devices on this account. Fetched once per session; used to bind
  // a specific dash-cam to each driver so completions auto-request footage.
  const [ravens, setRavens] = useState([]);
  const [ravensError, setRavensError] = useState(null);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showReset, setShowReset] = useState(null); // user obj
  const [newPwd, setNewPwd] = useState("");

  async function load() {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/routes")]);
    setUsers(u.data || []);
    setRoutes(r.data || []);
  }
  useEffect(() => { load(); }, []);

  // Load Raven devices on mount — non-blocking. If the integration isn't
  // configured yet (503) or the API is down (502), we show a hint next to
  // the picker but never break the page.
  useEffect(() => {
    api.get("/dashcam/ravens")
      .then(({ data }) => setRavens(data?.ravens || []))
      .catch((err) => setRavensError(err.response?.data?.detail || "Raven API unreachable"));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) =>
      [u.name, u.username, u.email, u.phone, u.role]
        .filter(Boolean).join(" ").toLowerCase().includes(t)
    );
  }, [users, q]);

  function openCreate() {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  }

  function openEdit(u) {
    setEditing(u);
    setForm({
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      password: "",
      phone: u.phone || "",
      assigned_route_id: u.assigned_route_id || (u.routes?.[0]?.id || ""),
      status: u.status || "active",
      role: u.role === "visitor" ? "visitor" : "driver",
      raven_device_id: u.raven_device_id || "",
    });
    setShowForm(true);
  }

  async function submit(e) {
    e.preventDefault();
    try {
      if (editing) {
        const payload = {
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          assigned_route_id: form.role === "visitor" ? null : (form.assigned_route_id || null),
          status: form.status,
          role: form.role,
          raven_device_id: form.role === "visitor" ? null : (form.raven_device_id || null),
        };
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.password) {
          toast.error("Password required for new staff");
          return;
        }
        await api.post("/users/staff", {
          first_name: form.first_name,
          last_name: form.last_name,
          password: form.password,
          phone: form.phone,
          assigned_route_id: form.role === "visitor" ? null : (form.assigned_route_id || null),
          status: form.status,
          role: form.role,
          raven_device_id: form.role === "visitor" ? null : (form.raven_device_id || null),
        });
        toast.success(`${form.role === "visitor" ? "Mentor" : "Staff"} created · username ${genUsername(form.first_name, form.last_name)}`);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  }

  async function toggleStatus(u) {
    const next = u.status === "inactive" ? "active" : "inactive";
    try {
      await api.put(`/users/${u.id}`, { status: next });
      toast.success(`${u.name} ${next === "active" ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  }

  async function del(u) {
    if (!window.confirm(`Permanently delete ${u.name}?\nThey will lose access immediately.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  async function submitReset(e) {
    e.preventDefault();
    if (!newPwd || newPwd.length < 4) {
      toast.error("Password too short");
      return;
    }
    try {
      await api.post(`/users/${showReset.id}/reset-password`, { new_password: newPwd });
      toast.success(`Password reset for ${showReset.name}`);
      setShowReset(null);
      setNewPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  }

  const previewUsername = genUsername(form.first_name, form.last_name);

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#5C738A]">Workforce</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[#012340] mt-1">User Management</h1>
          <p className="text-sm text-[#5C738A] mt-1">Add staff, assign routes, reset passwords, and enable/disable accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C738A]" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              data-testid="users-search"
              className="pl-9 pr-3 py-2 rounded-sm bg-white border border-[#012340]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]"
            />
          </div>
          <button className="btn-primary" onClick={openCreate} data-testid="users-new-btn">
            <Plus size={16} weight="bold" className="mr-1.5" /> Add Staff
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#012340]/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-[#5C738A]">
            <tr className="text-left">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Username / Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Assigned Route</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const routesAssigned = u.routes && u.routes.length ? u.routes : [];
              return (
                <tr key={u.id} className="even:bg-[#FAFAFA] border-t border-[#012340]/5" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-semibold text-[#012340]">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-sm bg-[#012340] text-[#CFAB7A] grid place-items-center"><IdentificationCard size={14} weight="bold" /></div>
                      {u.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#012340]">{u.username || u.email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold ${u.role === "admin" ? "bg-[#CFAB7A]/20 text-[#012340]" : "bg-[#012340]/10 text-[#012340]"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#5C738A]">{u.phone || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {routesAssigned.length ? routesAssigned.map((r) => (
                      <span key={r.id} className="inline-flex items-center gap-1 px-2 py-0.5 mr-1 rounded-sm bg-[#CFAB7A]/15 text-[#012340] font-bold">
                        <MapTrifold size={10} weight="bold" /> #{r.route_number}
                      </span>
                    )) : <span className="text-[#5C738A]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold ${u.status === "inactive" ? "bg-[#D32F2F]/10 text-[#D32F2F]" : "bg-[#2E7D32]/10 text-[#2E7D32]"}`}>
                      {u.status || "active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {u.role !== "admin" && (
                      <button onClick={() => toggleStatus(u)} className="p-1.5 rounded-sm hover:bg-[#012340]/5" title={u.status === "inactive" ? "Enable" : "Disable"} data-testid={`user-toggle-${u.id}`}>
                        {u.status === "inactive" ? <CheckCircle size={16} weight="bold" className="text-[#2E7D32]" /> : <Power size={16} weight="bold" className="text-[#F57C00]" />}
                      </button>
                    )}
                    <button onClick={() => setShowReset(u)} className="p-1.5 rounded-sm hover:bg-[#012340]/5" title="Reset password" data-testid={`user-reset-${u.id}`}>
                      <Key size={16} weight="bold" />
                    </button>
                    {u.role !== "admin" && (
                      <button onClick={() => openEdit(u)} className="p-1.5 rounded-sm hover:bg-[#012340]/5" data-testid={`user-edit-${u.id}`}>
                        <PencilSimple size={16} weight="bold" />
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button onClick={() => del(u)} className="p-1.5 rounded-sm hover:bg-[#D32F2F]/10 text-[#D32F2F]" data-testid={`user-del-${u.id}`}>
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5C738A]">No users match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-lg rounded-sm border border-[#012340]/10" data-testid="user-form">
            <div className="px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{editing ? "Edit" : "New"}</div>
                <div className="font-heading text-xl font-bold text-[#012340]">Staff Account</div>
              </div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18} weight="bold" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="First Name" required>
                <input className={inputCls} required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="uf-first" />
              </Field>
              <Field label="Last Name" required>
                <input className={inputCls} required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="uf-last" />
              </Field>
              <Field label="Auto-Generated Username" wide>
                <div className="px-3 py-2 rounded-sm bg-[#FAFAFA] border border-[#012340]/15 font-mono text-sm text-[#012340]">
                  {previewUsername || <span className="text-[#5C738A]">ZMX… (fill first + last name)</span>}
                </div>
                <div className="text-[10px] text-[#5C738A] mt-1">Format: <span className="font-mono">ZMX</span> + first letter of last name + full first name.</div>
              </Field>
              {!editing && (
                <Field label="Password" required wide>
                  <input type="password" className={inputCls} required minLength={4} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="uf-pwd" />
                </Field>
              )}
              <Field label="Phone Number">
                <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="uf-phone" />
              </Field>
              <Field label="Role" wide>
                <div className="flex gap-2">
                  {[
                    { v: "driver", l: "Driver (Truck / Tablet)" },
                    { v: "visitor", l: "Visitor / Mentor" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm({ ...form, role: opt.v, assigned_route_id: opt.v === "visitor" ? "" : form.assigned_route_id })}
                      data-testid={`uf-role-${opt.v}`}
                      className={`flex-1 px-4 py-2 rounded-sm text-xs font-bold border transition-all ${form.role === opt.v ? "bg-[#012340] text-white border-[#012340]" : "bg-white text-[#012340] border-[#012340]/20 hover:border-[#012340]/40"}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
                {form.role === "visitor" && (
                  <div className="text-[11px] text-[#5C738A] mt-1.5 leading-relaxed" data-testid="uf-visitor-hint">
                    Read-only account for a mentor or investor. Sees the whole dashboard,
                    reports, disposal tickets, containers, and route map pins — but customer
                    addresses, phones, emails, and notes are automatically redacted. All
                    edit / delete / create actions are blocked at the API level.
                  </div>
                )}
              </Field>
              {form.role === "driver" && (
                <Field label="Assigned Route">
                  <select className={inputCls} value={form.assigned_route_id} onChange={(e) => setForm({ ...form, assigned_route_id: e.target.value })} data-testid="uf-route">
                    <option value="">— None —</option>
                    {routes.map((r) => (
                      <option key={r.id} value={r.id}>#{r.route_number} {r.name ? `· ${r.name}` : ""}</option>
                    ))}
                  </select>
                </Field>
              )}
              {form.role === "driver" && (
                <Field label="Raven Dash Cam Device">
                  <select
                    className={inputCls}
                    value={form.raven_device_id}
                    onChange={(e) => setForm({ ...form, raven_device_id: e.target.value })}
                    data-testid="uf-raven-device"
                  >
                    <option value="">— None (no dash-cam) —</option>
                    {ravens.map((r) => {
                      // Raven vehicles come back with various shapes across accounts —
                      // Klashwerks currently returns `ravenUuid` as the canonical
                      // device id used in the /ravens/{id}/media endpoints. Fall
                      // back to older names so we survive schema changes.
                      const id = r.ravenUuid || r.uuid || r.id || r.ravenId || r.raven_id || r.serialNo;
                      const label = r.name || r.vehicleName || r.car_persona || r.serialNo || id;
                      return <option key={id} value={id}>{label}</option>;
                    })}
                  </select>
                  {ravensError ? (
                    <div className="text-[10px] text-[#D32F2F] mt-1" data-testid="uf-raven-error">
                      {ravensError} — set RAVEN_API_KEY / RAVEN_API_SECRET in the backend .env.
                    </div>
                  ) : ravens.length === 0 ? (
                    <div className="text-[10px] text-[#5C738A] mt-1">No Raven devices found on this account.</div>
                  ) : (
                    <div className="text-[10px] text-[#5C738A] mt-1">
                      Bind the driver&apos;s truck to a Raven Connected dash-cam. Every completed stop will auto-request a 2-min video.
                    </div>
                  )}
                </Field>
              )}
              <Field label="Status" wide>
                <div className="flex gap-2">
                  {[
                    { v: "active", l: "Active" },
                    { v: "inactive", l: "Inactive" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm({ ...form, status: opt.v })}
                      data-testid={`uf-status-${opt.v}`}
                      className={`px-4 py-1.5 rounded-sm text-xs font-bold border ${form.status === opt.v ? "bg-[#012340] text-white border-[#012340]" : "bg-white text-[#012340] border-[#012340]/20"}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="px-5 py-3 border-t border-[#012340]/10 flex justify-end gap-2">
              <button type="button" className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="user-save">{editing ? "Update" : "Create Staff"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Reset password modal */}
      {showReset && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setShowReset(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitReset} className="bg-white w-full max-w-md rounded-sm border border-[#012340]/10" data-testid="reset-pwd-form">
            <div className="px-5 py-4 border-b border-[#012340]/10">
              <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Reset Password</div>
              <div className="font-heading text-xl font-bold text-[#012340]">{showReset.name}</div>
              <div className="text-xs text-[#5C738A] mt-1 font-mono">{showReset.username || showReset.email}</div>
            </div>
            <div className="p-5">
              <Field label="New Password" required>
                <input type="password" autoFocus className={inputCls} required minLength={4} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="reset-pwd-input" />
              </Field>
            </div>
            <div className="px-5 py-3 border-t border-[#012340]/10 flex justify-end gap-2">
              <button type="button" className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold" onClick={() => { setShowReset(null); setNewPwd(""); }}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="reset-pwd-save">Reset Password</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]";
function Field({ label, children, required, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{label}{required && <span className="text-[#D32F2F]">*</span>}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


